import {FocusKeys} from '@primer/behaviors'
import {CheckCircleFillIcon, CircleIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, IconButton, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {useState} from 'react'

import type {IconColor, IconName} from '../types'
import {
  getBackgroundIconColorClassName,
  getDisplayName,
  getIconColorClassName,
  getIconComponent,
  supportedIconColors,
  supportedIconNames,
} from '../types'
import styles from './IconAndColorPicker.module.css'

interface IconAndColorPickerProps {
  selectedColor: IconColor
  selectedIcon: IconName
  onApply: (color: IconColor, icon: IconName) => void
}

export function IconAndColorPicker({selectedColor, selectedIcon, onApply}: IconAndColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false)

  const [currentColor, setCurrentColor] = useState<IconColor>(selectedColor)
  const [currentIcon, setCurrentIcon] = useState<IconName>(selectedIcon)

  const currentColorClassName = `${getBackgroundIconColorClassName(currentColor)} color-fg-on-emphasis`

  function onOpen() {
    setCurrentColor(selectedColor)
    setCurrentIcon(selectedIcon)
    setIsOpen(true)
  }

  function onApplyClick() {
    onApply(currentColor, currentIcon)
    setIsOpen(false)
  }

  return (
    <AnchoredOverlay
      open={isOpen}
      onOpen={onOpen}
      onClose={() => setIsOpen(false)}
      focusZoneSettings={{bindKeys: FocusKeys.ArrowAll | FocusKeys.Tab}}
      renderAnchor={anchorProps => (
        <IconButton
          {...anchorProps}
          icon={getIconComponent(selectedIcon)}
          className={getIconColorClassName(selectedColor)}
          aria-label="Change icon"
        />
      )}
    >
      <div className="p-2 mx-auto" style={{maxWidth: '420px'}}>
        <div className="p-1">
          <div className="color-fg-muted text-bold">Color</div>
          <div className={clsx('d-flex flex-wrap my-1', styles.gap_2)} role="radiogroup" aria-label="Color selection">
            {supportedIconColors.map(color => (
              <Tooltip text={`${getDisplayName(color)} color`} key={color} direction="n" delay="long">
                <IconButton
                  key={color}
                  icon={color === currentColor ? CheckCircleFillIcon : CircleIcon}
                  variant="invisible"
                  className={color === currentColor ? currentColorClassName : getIconColorClassName(color)}
                  role="radio"
                  aria-label={`${getDisplayName(color)} color`}
                  aria-checked={color === currentColor}
                  onClick={() => setCurrentColor(color)}
                />
              </Tooltip>
            ))}
          </div>
          <div className="color-fg-muted text-bold">Icon</div>
          <div className={clsx('d-flex flex-wrap my-1', styles.gap_2)} role="radiogroup" aria-label="Icon selection">
            {supportedIconNames.map(iconName => (
              <Tooltip text={`${getDisplayName(iconName)} icon`} key={iconName} direction="n" delay="long">
                <IconButton
                  key={iconName}
                  icon={getIconComponent(iconName)}
                  variant="invisible"
                  className={iconName === currentIcon ? currentColorClassName : 'color-fg-muted'}
                  role="radio"
                  aria-checked={iconName === currentIcon}
                  aria-label={`${getDisplayName(iconName)} icon`}
                  onClick={() => setCurrentIcon(iconName)}
                />
              </Tooltip>
            ))}
          </div>
        </div>
        <div className={clsx('d-flex flex-justify-end p-1', styles.gap_2)}>
          <Button onClick={() => setIsOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={onApplyClick}>
            Apply
          </Button>
        </div>
      </div>
    </AnchoredOverlay>
  )
}
